/**
 * Module 38 (WP-38.2) — Block Jam board VISUAL config: schema, typed loader,
 * in-bundle default.
 *
 * Mirrors `block-visual-config.ts` exactly: the single shipped, editable artifact
 * is `games/blockjam/config/board-visuals.json` (the Board Visuals Studio tab
 * writes it, WP-38.6; the free-slide renderer's `drawBoardChrome()` reads it,
 * WP-38.4). That file lives OUTSIDE the package `rootDir` (`src`), so the
 * compiled library cannot import it — the parity default is authored here as
 * `DEFAULT_BOARD_VISUAL_CONFIG` and a drift test (`board-visual-config.test.ts`)
 * asserts the JSON parses to exactly this value.
 *
 * Scope: this config describes the board's furniture only — surface, grid lines,
 * frame, canvas backdrop. It does NOT own the outer glow/segmented border accent,
 * gate styling, or cell shading/vignette (all deliberately deferred, see
 * `planning/38-blockjam-board-visuals/CLAUDE.md` §2 decision 5).
 *
 * `DEFAULT_BOARD_VISUAL_CONFIG` shipped WP-38.2 through WP-38.4 holding EXACTLY
 * the old hardcoded renderer literals — WP-38.4 (the pure refactor) had to not
 * change a pixel. WP-38.5 is what re-toned it to the blue-slate board; the old
 * literals live on, frozen, as `REFERENCE_BOARD_CONFIG`.
 */
import { z } from 'zod';
// ─── Schema ───────────────────────────────────────────────────────────────────
/**
 * Every pixel-valued knob below is authored at this reference cell size and
 * scaled linearly by the renderer for any other cell size, so one config looks
 * identical in the Studio preview and in the game — same convention as
 * `block-visual-config.ts`'s `BASE_CELL`. It also happens to equal the real
 * renderer's fixed `CELL` constant, so the shipped default needs no scaling to
 * reproduce today's literals exactly.
 */
export const BASE_CELL = 72;
const hexColour = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex colour like #0b0c1a');
/**
 * The board's play-area fill. `gradientFrom`/`gradientTo`/`gradientAngle` are
 * only read when `mode === 'gradient'` — kept present either way so switching
 * modes in the Studio never loses the other mode's values.
 */
const surfaceSchema = z.object({
    mode: z.enum(['solid', 'gradient']),
    colour: hexColour,
    gradientFrom: hexColour,
    gradientTo: hexColour,
    /** Gradient direction in degrees; unused when `mode` is `solid`. */
    gradientAngle: z.number().min(0).max(360),
    /** Corner rounding of the surface rect, at `BASE_CELL`. */
    cornerRadius: z.number().min(0).max(60),
});
/** The lines separating cells. */
const gridSchema = z.object({
    colour: hexColour,
    alpha: z.number().min(0).max(1),
    /** Stroke width at `BASE_CELL`. */
    width: z.number().min(0).max(10),
});
/**
 * The outer border between the board's edge and the surface — the element that
 * has to hold contrast against both the surface (locked decision, `CLAUDE.md`
 * §7) and whatever sits behind the canvas (Module 34 pool backgrounds, checked
 * at WP-38.8). `thickness` was cosmetic-only under Module 38 (a caller-supplied
 * paint offset); Module 40 (WP-40.3) wires it to live layout — see `CLAUDE.md`
 * §2 decision 3.
 */
const frameSchema = z.object({
    colour: hexColour,
    /** How far the frame inset the surface, at `BASE_CELL`. */
    thickness: z.number().min(0).max(80),
    /** Corner rounding of the outer frame rect, at `BASE_CELL`. */
    cornerRadius: z.number().min(0).max(60),
});
/**
 * Module 40 (WP-40.5). Shared by the frame AND every gate (`CLAUDE.md` §2
 * decision 7) so the two can never drift apart the way they did before this
 * module. Gates override only their fill colour, taken from the block
 * palette — never from here.
 */
const materialSchema = z.object({
    style: z.enum(['flat', 'bevel', 'raised', 'inset']),
    highlightColour: hexColour,
    shadowColour: hexColour,
    /** Bevel rim width, at `BASE_CELL`. */
    bevelWidth: z.number().min(0).max(20),
    /** How many low-alpha passes feather the rim. */
    bevelSoftness: z.number().min(0).max(1),
    /** Overall opacity of the whole bevel. */
    bevelStrength: z.number().min(0).max(1),
    /**
     * Where the light comes from, in the SAME convention as
     * `surface.gradientAngle` — CSS `linear-gradient`: 0° up, increasing
     * clockwise. So the default 315° is the top-left, per `CLAUDE.md` §3.
     * (WP-40.1 wrote "0 = right" here; WP-40.5, which is what actually reads
     * this field, resolves it through the config's one shared angle helper.)
     */
    lightAngle: z.number().min(0).max(360),
});
/**
 * Module 40 (WP-40.6–40.8). Gate geometry and arrow knobs. Colour is
 * deliberately absent — gates always take their fill from the block palette
 * (`CLAUDE.md` §2 decision 7).
 */
const gatesSchema = z.object({
    /** × `frame.thickness`. `1.0` = flush with the ring band, no gap/overhang. */
    fillRatio: z.number().min(0.1).max(1),
    /** Corner rounding of the gate capsule, at `BASE_CELL`. */
    cornerRadius: z.number().min(0).max(30),
    /** How far short of its cell span a gate stops, at `BASE_CELL`. */
    lengthInset: z.number().min(0).max(30),
    glowOpacity: z.number().min(0).max(1),
    /** Glow halo spread beyond the gate rect, at `BASE_CELL`. */
    glowSpread: z.number().min(0).max(20),
    arrow: z.object({
        enabled: z.boolean(),
        /** At `BASE_CELL`. */
        size: z.number().min(1).max(30),
        opacity: z.number().min(0).max(1),
    }),
});
/**
 * The Pixi `Application` background behind the frame — visible only through
 * the outer frame rect's own rounded corners. In scope because it is the same
 * regression as the rest of the board (`CLAUDE.md` §3): leaving it hardcoded
 * while everything else became tunable would just relocate the bug.
 * `alpha` (Module 40, WP-40.2) lets the board float over the app background
 * transparently instead of sitting on an opaque backdrop.
 */
const canvasSchema = z.object({
    colour: hexColour,
    alpha: z.number().min(0).max(1),
});
/**
 * Module 63 (WP-63.A1) — Powered Gates chrome: the shutter over a closed gate,
 * its indicator lamp, the pressure plate on the board floor, the trace drawn
 * between them, and the timings of the three transitions.
 *
 * **Inert on every board that has no powered gate.** Nothing in this section is
 * read until an `Exit` carries a `powered` clause (WP-63.B1), so a config that
 * only adds these knobs cannot change a pixel of a board without one — proved
 * at WP-63.A1 by canvas captures hashed before and after the section landed.
 * Colour is deliberately NOT here for the plate or the trace: both take their
 * gate's palette colour (`CLAUDE.md` §3 decision 12), the way gates already do.
 */
const powerSchema = z.object({
    /** The drawn shutter that covers a closed gate's opening. */
    shutter: z.object({
        colour: hexColour,
        opacity: z.number().min(0).max(1),
        /** Horizontal slats drawn across the shutter; `0` = one solid plate. */
        slatCount: z.number().int().min(0).max(8),
        /** Gap between slats, at `BASE_CELL`. */
        slatGap: z.number().min(0).max(10),
        /** Corner rounding of the shutter rect, at `BASE_CELL`. */
        cornerRadius: z.number().min(0).max(30),
        /**
         * Module 66 (WP-66.C1, sample 3 "Bold") — the lock icon at the shutter's
         * centre, radius at `BASE_CELL`; `0` = none. Defaulted so every pre-66
         * config (fixtures included) still parses.
         */
        lockRadius: z.number().min(0).max(20).default(11),
        /** Chevrons in the gate's colour near the shutter's ends; `0` = none. At `BASE_CELL`. */
        chevronSize: z.number().min(0).max(20).default(6),
    }),
    /** The small indicator lamp beside a powered gate — lit when open, dark when shut. */
    lamp: z.object({
        /** At `BASE_CELL`. */
        radius: z.number().min(1).max(12),
        /** Distance from the gate capsule's end, at `BASE_CELL`. */
        offset: z.number().min(0).max(20),
        litColour: hexColour,
        darkColour: hexColour,
        /** Glow halo spread beyond the lit lamp, at `BASE_CELL`; `0` = no glow. */
        litGlow: z.number().min(0).max(20),
    }),
    /** The pressure plate, a floor feature drawn inside its cell. */
    plate: z.object({
        /** Inset from the cell edge, at `BASE_CELL`. */
        inset: z.number().min(0).max(30),
        /** At `BASE_CELL`. */
        cornerRadius: z.number().min(0).max(30),
        /** Ring stroke width, at `BASE_CELL`. */
        ringWidth: z.number().min(0).max(10),
        litAlpha: z.number().min(0).max(1),
        darkAlpha: z.number().min(0).max(1),
        /**
         * Module 66 (WP-66.C1, sample 3 "Bold") — the pressed-button rim: a light
         * band on the plate's top edge and a shadow band on its bottom, `rim` px
         * deep at `BASE_CELL`, that make the dark plate read as a physical button
         * rather than an empty cell (the shipped defect). `0` = flat, pre-66 paint.
         */
        rim: z.number().min(0).max(12).default(6),
        /** The ⚡ bolt on the dark plate's face, as a scale of the plate; `0` = none. */
        boltScale: z.number().min(0).max(1.5).default(0.95),
        /**
         * Module 77 (D1, picked sample E2) — what the plate's OWN paint is.
         * `'gate'` is the Module 63/66 plate, tinted its gate's colour (decision
         * 12). `'neutral'` paints it `neutralHex` instead, because play-testers
         * read a yellow plate as "needs a yellow block" — and parking the yellow
         * block there is the one move that loses. The trace keeps the gate's
         * colour either way (it is what ties a plate to its gate), and while
         * Surge is active every plate keeps the `'gate'` look (D1: "except under
         * Surge"). Default `'gate'`, so a config written before Module 77 still
         * draws the plate it always drew.
         */
        style: z.enum(['gate', 'neutral']).default('gate'),
        /**
         * Module 77 (D1) — the mark on the plate's face. `'bolt'` is Module 66's
         * ⚡. `'slot'` is a block-slot outline stroked in EVERY block colour, one
         * equal segment each, taken from `block-visuals.json`'s palette at
         * runtime (so a palette edit follows) — "any block goes here". Off under
         * Surge, like `style`. `boltScale: 0` removes either mark.
         */
        glyph: z.enum(['bolt', 'slot']).default('bolt'),
        /** Module 77 — the plate's face/ring/halo colour when `style` is `'neutral'`. */
        neutralHex: hexColour.default('#8a93a6'),
        /** Module 77 — the `'slot'` glyph's stroke width, at `BASE_CELL`. */
        slotWidth: z.number().min(0.5).max(8).default(2.4),
        /**
         * Module 80 (WP-80.11) — a small lightning bolt in the plate's corner, in
         * its GATE's colour, so the plate and the padlock read as a pair: this is
         * where the power comes from. Off by default so every earlier config, and
         * every call log pinned to one, paints exactly what it did.
         */
        cornerBolt: z.boolean().default(false),
        /**
         * Module 80 (WP-80.11b) — a small disc in the GATE's colour at the plate's
         * centre with a dark keyhole cut into it (user pick D4, 2026-10-02). It
         * replaced the corner bolt, which testers could not see at phone size; the
         * keyhole pairs the plate with the padlock on its gate. Off by default.
         */
        keyhole: z.boolean().default(false),
    }),
    /** The trace from plate to gate, drawn on the grid lines. */
    trace: z.object({
        /** Stroke width, at `BASE_CELL`. */
        width: z.number().min(0.5).max(12),
        litAlpha: z.number().min(0).max(1),
        darkAlpha: z.number().min(0).max(1),
        /** Glow halo spread beyond the lit trace, at `BASE_CELL`; `0` = no glow. */
        glowSpread: z.number().min(0).max(20),
        /** Dash gap of the DARK trace, at `BASE_CELL`; `0` = solid. */
        dashGap: z.number().min(0).max(20),
        /**
         * Module 66 (WP-66.C1) — the dark trace's inner CHANNEL stroke: a darker
         * line inside the coloured one that makes an unpowered wire read as a
         * conduit sunk into the floor rather than a painted stripe (the shipped
         * dotted line read as neither). `0` = plain stroke, pre-66 paint.
         */
        channelAlpha: z.number().min(0).max(1).default(0.8),
        /** Bright energy beads along a LIT trace; `0` = none. */
        beadCount: z.number().int().min(0).max(6).default(3),
        /**
         * Module 66 (sign-off fix) — the trace's OVER-BLOCKS echo: a slim second
         * stroke of the same route drawn ABOVE the blocks, so the plate→gate
         * connection stays readable when blocks sit on the grid lines it follows
         * (the floor trace is under every block by design, and on a busy board
         * that buried it — the shipped defect). This is the echo's opacity;
         * `0` = no echo, pre-66 paint.
         */
        overlayAlpha: z.number().min(0).max(1).default(0.55),
        /**
         * Module 80 (WP-80.11) — beads FLOW from the plate to the gate, taking
         * this long to travel the whole wire. Testers read a static lit wire as
         * "the path to follow"; motion from plate to gate reads as power going
         * somewhere. `0` keeps Module 66's static beads (and every pin made with
         * them). Always static under reduced motion.
         */
        flowMs: z.number().min(0).max(8000).default(0),
    }),
    /**
     * Module 68 (WP-68.4) — how a SHUT gate is painted, replacing the Module 66
     * shutter's five-marks-in-one-cell.
     *
     * ## The measurement this block exists because of
     *
     * The board draws in a fixed logical space — `CELL = 72`, `frame.thickness =
     * 25` — and the whole canvas is then scaled by CSS, so `scale = cell /
     * BASE_CELL` is **always 1** and a span-1 gate capsule is **always 25 × 68
     * px**, on every device. Into that the Bold shutter put a near-opaque plate,
     * two slat gaps, two chevrons, a lock badge clamped only against
     * `min(w,h)/2 − 1` (so **22px across a 25px capsule**) and a dark lamp.
     * Nothing clamped by available space, so a one-cell gate got everything a
     * three-cell gate got. Reported 2026-09-22 (`Bugs/Intermediate_2.jpeg`):
     * "very very congested and confusing and not clear".
     *
     * ## Every field defaulted, on purpose
     *
     * `schemaVersion` stays `3`: a config written before this module — the Studio
     * presets, every test fixture, a user's saved candidate — parses unchanged
     * and gets these defaults. `style: 'plate'` re-enters the Module 66 path
     * exactly, which is how its call-log pins keep describing the thing they were
     * written about.
     */
    closed: z
        .object({
        /**
         * `plate` — Module 66's dark plate over the opening, with its slats,
         * chevrons and centred lock (kept, so the old look is a dropdown away).
         * `drained` — no overlay at all: the capsule is painted in a desaturated,
         * darkened version of its OWN colour. `faded` — full hue at low alpha,
         * disabled-button language.
         *
         * Colour is never dropped in any of them: Module 63 decision 10 stands —
         * grey would read as "no colour assigned" in a game whose language is
         * colour.
         */
        style: z.enum(['plate', 'drained', 'faded']).default('drained'),
        /** Whether the lock badge is drawn at all. */
        lock: z.boolean().default(true),
        /**
         * `indicator` — the lamp's own slot at the capsule's first end, the lock
         * REPLACING the dark lamp (one indicator slot, two states). `centre` —
         * the middle of the capsule.
         */
        lockPlace: z.enum(['indicator', 'centre']).default('indicator'),
        /** Whether the gate's outward arrow survives the gate being shut. */
        arrow: z.boolean().default(true),
        /** `centre` — where an open gate's arrow is. `end` — the capsule's far end. */
        arrowPlace: z.enum(['centre', 'end']).default('centre'),
        /** The closed arrow's size as a fraction of `gates.arrow.size`. */
        arrowScale: z.number().min(0.2).max(1).default(0.7),
        /** `drained`: how much of the gate's own saturation survives. `0` = its own grey. */
        saturation: z.number().min(0).max(1).default(0.45),
        /** `drained`: how bright the drained colour stays. `1` = no darkening. */
        darkness: z.number().min(0).max(1).default(0.62),
        /** `faded`: the shut capsule's opacity. */
        fadeAlpha: z.number().min(0.05).max(1).default(0.4),
        /**
         * Module 80 (WP-80.10) — the padlock STAYS when the gate opens, with its
         * shackle lifted (🔓), instead of fading out and leaving the lamp dot a
         * tester called "an irrelevant circle". The lamp is not drawn. The
         * change between the two is the transition itself: shackle pops open
         * with a flash, snaps shut with a shake. Off by default (Module 68's
         * fade-out-to-lamp), so earlier configs and their pins are untouched.
         */
        openLock: z.boolean().default(false),
        /**
         * Module 80 (WP-80.12) — on a player's first N powered puzzles a SHUT
         * padlock gives a small wiggle every few seconds, then never again.
         * `0` disables it.
         */
        attractPuzzles: z.number().int().min(0).max(10).default(0),
        /**
         * Module 80 (WP-80.10b) — the BOLD padlock (user pick M2, 2026-10-02):
         * about 30% bigger (the radius may take 0.44 of the capsule's thickness
         * instead of 0.34), a thicker shackle with a dark outline, and a keyhole
         * dot on the body. The 9px lock was unreadable on a phone. Off by default.
         */
        lockBold: z.boolean().default(false),
        /**
         * Module 80 (WP-80.10b) — how far an OPEN shackle leans back on its left
         * leg, in degrees: the whole shackle pivots at that leg's foot, so the
         * free right end rises up and away and the gap is wide. 0 is the plain
         * lift (🔓) shipped first.
         */
        openTilt: z.number().min(0).max(90).default(0),
        /** Module 80 (WP-80.10b) — the open shackle's lift, × the lock radius. */
        openRise: z.number().min(0).max(1.5).default(0.5),
        /**
         * Module 81 (WP-81.1, user pick F) — fit the OPEN padlock to its gate
         * (`fitOpenLock`): never across the capsule's sides, a straight lift on
         * side gates, lift lowered before the lock shrinks, and the arrow kept
         * clear. The SHUT lock is untouched. Off by default, so earlier configs
         * and their pins draw exactly what they drew.
         */
        openFit: z.boolean().default(false),
        /** Module 81 — with `openFit`, the open lock's gap to the open arrow (BASE_CELL px). */
        openArrowGap: z.number().min(0).max(20).default(4.5),
    })
        // `.prefault`, NOT `.default`: in zod 4 a `.default(value)` SHORT-CIRCUITS —
        // the value is returned verbatim without being parsed — so `.default({})`
        // on an all-defaulted object yields literally `{}` and every field reads
        // `undefined`. Which does not throw; it propagates, and `saturation:
        // undefined` came out the far end of `mixColour` as a NaN channel painted
        // as black. `.prefault` runs the default THROUGH the schema, which is what
        // makes "every field defaulted" actually true.
        .prefault({}),
    /** Transition timings. Not scaled by cell size. */
    motion: z.object({
        /** Trace draining/lighting as a plate is uncovered/covered. */
        drainMs: z.number().min(0).max(2000),
        /** Shutter dropping/retracting. */
        shutterMs: z.number().min(0).max(2000),
        /** One refusal pulse, plate → gate, when a block is stopped by a closed gate. */
        pulseMs: z.number().min(0).max(4000),
    }),
});
export const boardVisualConfigSchema = z.object({
    schemaVersion: z.literal(3),
    surface: surfaceSchema,
    grid: gridSchema,
    material: materialSchema,
    frame: frameSchema,
    gates: gatesSchema,
    power: powerSchema,
    canvas: canvasSchema,
});
// ─── Loader ───────────────────────────────────────────────────────────────────
/**
 * Parse + validate an untyped config object (the JSON file the Studio wrote, or
 * a fixture). Throws a `ZodError` with a readable path on invalid input.
 */
export function loadBoardVisualConfig(raw) {
    return boardVisualConfigSchema.parse(raw);
}
// ─── Default (today's hardcoded renderer literals, unchanged until WP-38.5) ───
/**
 * Kept in sync with `games/blockjam/config/board-visuals.json` — the drift test
 * enforces `loadBoardVisualConfig(json)` deep-equals this value.
 *
 * WP-38.5 (the module's first visible change) re-toned these from the old
 * near-black literals to a mid-dark blue-slate board:
 *
 * - `surface` `#3a4570` — the target locked at the 2026-08-01 interview
 *   (`CLAUDE.md` §2 decision 1). Deliberately darker than the reference
 *   image's ~`#6b7ba8`, so the user's Studio-tuned `block-visuals.json`
 *   bevel/gloss/shadow/glow values do not need a re-tune.
 * - `grid` `#8fa0d8` @ 0.55 — a light blue that reads clearly against the
 *   surface, replacing `#252845` @ 0.55 which was all but invisible on
 *   near-black. Widened 1 → 1.5px so the lines survive a phone screen.
 * - `frame` `#1e2447` — now clearly DARKER than the surface. The old
 *   `#0f1122` frame vs `#0b0c1a` surface were nearly the same colour, which
 *   is half of why the board read as one flat black rectangle (`CLAUDE.md`
 *   §7); a dark frame also holds the play area's boundary against the lighter
 *   Module 34 pool backgrounds (verified at WP-38.8).
 * - `canvas` `#12162e` — darker still, so the sliver visible through the
 *   frame's rounded corners recedes rather than glowing.
 *
 * `mode` stays `solid`; `gradientFrom`/`gradientTo` are carried as sensible
 * blue values around the surface so flipping the mode in the Studio is
 * meaningful, but the shipped board is flat (a gradient surface is not
 * implemented — see `board-chrome.ts`).
 *
 * Module 40 (WP-40.1) added `material`, `gates` and `canvas.alpha` at their
 * **shipping** values — decision 12 ("shape changes ship, colours don't"):
 * `style: 'bevel'` at restrained strength, `fillRatio: 1.0` (flush gates),
 * `arrow.enabled: true`, `canvas.alpha: 0` (fully transparent).
 *
 * At module close (WP-40.11) the user hand-tuned this default live in the
 * Board Visuals Studio and asked for the result to ship, superseding
 * decision 12's "colours don't ship" clause for this module only: `surface`
 * `#2f385b`, `frame` `#5a5d6c` @ thickness 25 / radius 25, a softer/wider
 * bevel (`bevelSoftness 1`, `bevelStrength 0.2`, `lightAngle 270`), rounder
 * flush gates (`cornerRadius 15`, `lengthInset 2`) and larger arrows
 * (`size 18`, `opacity 0.85`). `grid` and `canvas` are untouched.
 */
export const DEFAULT_BOARD_VISUAL_CONFIG = loadBoardVisualConfig({
    schemaVersion: 3,
    surface: {
        mode: 'solid',
        colour: '#2f385b',
        gradientFrom: '#414d7d',
        gradientTo: '#333d63',
        gradientAngle: 180,
        cornerRadius: 8,
    },
    grid: { colour: '#8fa0d8', alpha: 0.55, width: 1.5 },
    material: {
        style: 'bevel',
        highlightColour: '#ffffff',
        shadowColour: '#000000',
        bevelWidth: 2.5,
        bevelSoftness: 1,
        bevelStrength: 0.2,
        lightAngle: 270,
    },
    frame: { colour: '#5a5d6c', thickness: 25, cornerRadius: 25 },
    gates: {
        fillRatio: 1,
        cornerRadius: 15,
        lengthInset: 2,
        glowOpacity: 0.2,
        glowSpread: 3,
        arrow: { enabled: true, size: 18, opacity: 0.85 },
    },
    power: {
        // Module 66 (WP-66.C1) — sample 3 "Bold", chosen by the user 2026-09-03:
        // the shutter gains a lock + chevrons, the plate becomes a pressed button
        // with a bolt, and the dark trace becomes a SOLID conduit (dashGap 0 +
        // channel) instead of the dotted line the play-test called unreadable.
        shutter: {
            colour: '#1a1d2e',
            opacity: 0.85,
            slatCount: 3,
            slatGap: 2,
            cornerRadius: 10,
            lockRadius: 11,
            chevronSize: 6,
        },
        lamp: { radius: 4, offset: 6, litColour: '#fff4c2', darkColour: '#2a2d3e', litGlow: 6 },
        plate: {
            inset: 10,
            cornerRadius: 8,
            ringWidth: 3,
            litAlpha: 0.9,
            darkAlpha: 0.45,
            rim: 6,
            boltScale: 0.95,
            // Module 77 (D1) — sample E2, picked by the user 2026-09-28: a neutral
            // steel plate whose ⚡ became a block-slot outline in every block colour.
            style: 'neutral',
            glyph: 'slot',
            neutralHex: '#8a93a6',
            slotWidth: 2.4,
            // Module 80 (WP-80.11b) — the corner bolt was invisible on a phone; a
            // gate-coloured keyhole disc at the centre replaced it (user pick D4).
            cornerBolt: false,
            keyhole: true,
        },
        trace: {
            width: 4,
            litAlpha: 0.95,
            darkAlpha: 0.35,
            glowSpread: 4,
            dashGap: 0,
            channelAlpha: 0.8,
            beadCount: 3,
            overlayAlpha: 0.55,
            // Module 80 (WP-80.11) — beads flow plate → gate.
            flowMs: 1600,
        },
        // Module 68 — the shut gate, hand-tuned in the Studio by the user
        // 2026-09-22. Stated here rather than left to the schema's own defaults for
        // the same reason every other section is: the schema default answers "what
        // does a config written before this block get", and the shipped look is a
        // separate question with a separate answer. `arrowScale` and `darkness` are
        // the two the tuning pass moved.
        closed: {
            style: 'drained',
            lock: true,
            lockPlace: 'indicator',
            arrow: true,
            arrowPlace: 'centre',
            arrowScale: 0.85,
            saturation: 0.45,
            darkness: 0.65,
            fadeAlpha: 0.4,
            // Module 80 (WP-80.10/12) — the padlock stays and opens (🔓) instead of
            // fading to the lamp; a shut lock wiggles on the first 3 powered puzzles.
            openLock: true,
            attractPuzzles: 3,
            // Module 80 (WP-80.10b) — user pick M2: bigger outlined lock, shackle
            // lifted 0.7 radius and leaning back 60°.
            lockBold: true,
            // Module 81 (WP-81.1) — user pick F: tip 60° → 45°, and the open lock is
            // fitted to its gate with a gap to the arrow.
            openTilt: 45,
            openRise: 0.7,
            openFit: true,
            openArrowGap: 4.5,
        },
        // Module 80 (WP-80.10) — 180 → 350ms, eased in the paint: the shackle pop
        // has to be seen to be read, and 180ms linear was a blink.
        motion: { drainMs: 220, shutterMs: 350, pulseMs: 600 },
    },
    canvas: { colour: '#12162e', alpha: 0 },
});
/**
 * Frozen independently of `DEFAULT_BOARD_VISUAL_CONFIG` — a SEPARATE literal
 * still holding the pre-WP-38.5 near-black literals, unchanged by that WP's
 * re-tone and by any future one. Renderer-maths tests (WP-38.4's bit-for-bit
 * identity test, and any future board-chrome geometry test) key off this
 * instead, per the precedent set by `block-visual-render.test.ts`'s local
 * `REFERENCE_CONFIG` — so tuning the shipped defaults never breaks a geometry
 * assertion. As of WP-38.5 this deliberately no longer equals the default.
 *
 * Module 40 (WP-40.1) added `material`, `gates` and `canvas.alpha` at
 * **reproduce-today** values, matching the pre-Module-40 hardcoded literals in
 * `free-slide-player.ts`'s `drawGate` (gate thickness `13` on a `frame.thickness`
 * of `20` ⇒ `fillRatio` `13/20`, `PAD` `6` ⇒ `lengthInset` `6`, corner radius `4`,
 * glow radius `6` ⇒ `glowSpread` `2`, glow alpha `0.18`) and `style: 'flat'` /
 * `arrow.enabled: false` / `canvas.alpha: 1` so this literal keeps reproducing
 * the exact pre-Module-40 board. `power` (Module 63) carries the same values
 * as the default: it draws nothing on a board without a powered gate, and the
 * geometry tests that key off this literal have none.
 */
export const REFERENCE_BOARD_CONFIG = loadBoardVisualConfig({
    schemaVersion: 3,
    surface: {
        mode: 'solid',
        colour: '#0b0c1a',
        gradientFrom: '#0b0c1a',
        gradientTo: '#0b0c1a',
        gradientAngle: 180,
        cornerRadius: 8,
    },
    grid: { colour: '#252845', alpha: 0.55, width: 1 },
    material: {
        style: 'flat',
        highlightColour: '#ffffff',
        shadowColour: '#000000',
        bevelWidth: 0,
        bevelSoftness: 0,
        bevelStrength: 0,
        lightAngle: 315,
    },
    frame: { colour: '#0f1122', thickness: 20, cornerRadius: 14 },
    gates: {
        fillRatio: 13 / 20,
        cornerRadius: 4,
        lengthInset: 6,
        glowOpacity: 0.18,
        glowSpread: 2,
        arrow: { enabled: false, size: 8, opacity: 0.9 },
    },
    // BORROWED from the default, not frozen alongside the rest of this literal.
    // `REFERENCE_BOARD_CONFIG` is the pre-WP-38.5 board, and `power` did not exist
    // then — there is no historic powered look to preserve, so pinning one here
    // would be inventing history. It was duplicated at WP-63.A1 and the parity
    // test caught the copies diverging on the first `power.*` tune; sharing the
    // object makes that parity structural instead of something to remember.
    power: DEFAULT_BOARD_VISUAL_CONFIG.power,
    canvas: { colour: '#0b0c1a', alpha: 1 },
});
